import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import spainmundo from "../../assets/sponsors/spainmundo.png";
import agreaterstar from "../../assets/sponsors/agreaterstar.png";
import mett from "../../assets/sponsors/mett.png";
import theflag from "../../assets/sponsors/theflag.png";
import spisa from "../../assets/sponsors/spisa.png";
import middagskassen from "../../assets/sponsors/middagskassen.png";

const title = "Sponsors | Swedish International School Estepona";
const description =
  "The school's sponsors contribute to extra experiences for pupils. All proceeds go directly to pupils or to the school's family events.";

export const Route = createFileRoute("/en/sponsors")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/sponsors") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/sponsors"),
  }),
  component: Sponsors,
});

const sponsors = [
  {
    name: "Spainmundo",
    logo: spainmundo,
    url: "https://www.spainmundo.com/",
    text: "Move with confidence with Spainmundo – your partner for a smooth and hassle-free relocation to Spain. Our team of international relocation experts handles everything, from paperwork and housing to cultural adaptation.",
  },
  {
    name: "A Greater Star",
    logo: agreaterstar,
    url: "https://greaterstar.io",
    text: "Discover the endless possibilities of the digital world with A Greater Star, your trusted partner in web design and digital marketing, with expertise in web development, SEO and digital strategy.",
  },
  {
    name: "METT",
    logo: mett,
    text: "The 5-star lifestyle property METT Hotel & Beach Resort features 249 elegant and beautifully designed guest rooms and suites around Azure Beach's heated pool with uninterrupted views of the Mediterranean Sea.",
  },
  {
    name: "The Flag",
    logo: theflag,
    text: "THE FLAG Costa del Sol in Estepona near Marbella invites you to unwind – whether you want to enjoy a relaxing dream holiday or take an active approach to your wellbeing with our wellbeing experts and holistic program.",
  },
  {
    name: "Spisa",
    logo: spisa,
    url: "http://www.spisa.shop/",
    text: "Spisa foodmarket Marbella, Estepona. Scandinavian groceries, deli, bakery, catering! Open 9 to 21, 365 days a year.",
  },
  {
    name: "Middagskassen.se",
    logo: middagskassen,
    url: "https://www.middagskassen.se/",
    text: "Middagskassen.se compares Sweden's largest and most popular meal-kit delivery providers and helps you find the perfect solution for a stress-free everyday life.",
  },
];

function Sponsors() {
  return (
    <>
      <PageHero
        eyebrow="Partnerships"
        title="Sponsors"
        intro="The school partners with companies that help give pupils the chance to enjoy extra-special experiences. All proceeds from sponsors go directly to pupils or to one of our family events."
      />

      <Prose>
        <p>
          Thanks to support and contributions from sponsors, the school can offer, among other
          things, technical aids or other things that benefit pupils' schooling and everyday life.
          The school is a natural meeting place, and several times a year all families have the
          chance to meet outside school hours too – as a sponsor you can help make that happen.
        </p>
        <p>
          Would your company like to become a sponsor? Get in touch at{" "}
          <a href="mailto:info@sweintschool.com">info@sweintschool.com</a>.
        </p>

        <h2>What sponsorship is used for</h2>
        <p>
          Income from sponsors goes to the pupils' schooling and to the school's shared occasions —
          that may mean materials, equipment or costs around family events. The school is a small
          organisation, and this kind of support makes a real difference day to day.
        </p>

        <h2>Being a partner</h2>
        <p>
          As a sponsor, your company becomes part of a community of families from several countries
          living and working between Estepona and Marbella. Arrangements vary depending on what you want
          to contribute — financial support, products, services or taking part in an event. We are happy
          to discuss what suits your company.
        </p>


      </Prose>

      <div className="mx-auto grid max-w-5xl gap-6 px-5 pb-16 sm:grid-cols-2">
        {sponsors.map((s) => (
          <article key={s.name} className="rounded-xl border border-border bg-card p-7 shadow-soft">
            <div className="flex h-20 items-center">
              <img
                src={s.logo}
                alt={`${s.name} logo`}
                loading="lazy"
                className="max-h-16 w-auto max-w-[220px] object-contain"
              />
            </div>
            <h2 className="mt-5 text-lg font-semibold">{s.name}</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
            {s.url && (
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block text-sm text-primary link-quiet"
              >
                Visit website
              </a>
            )}
          </article>
        ))}
      </div>

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
